import { confirmApp } from '../common/appDialog';
import React, { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Organization, UserProfile } from '@types';
import { adminRoleLabel, adminRolesFor, adminAssignmentError, hasRegionalConsultantRole, type AdminRole, type AdminFunctionalRole, type Region } from '../common/adminAccess';
import AdminAssignmentFields from './AdminAssignmentFields';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import { openMfaRecovery } from '../common/mfa';
import { mfaStyles } from './mfaStyles';
import { managementButtonStyles as buttonStyles } from '../common/managementButtonStyles';

interface Props {
    currentUser: UserProfile;
    editEmail?: string | null;
    onEditHandled?: () => void;
}

interface FormState {
    email: string;
    displayName: string;
    adminRole: AdminRole;
    adminRoles: AdminFunctionalRole[];
    regions: Region[];
    isTestConsultant: boolean;
    organizationId: string;
    status: 'active' | 'pending' | 'suspended' | 'pending_approval';
}

const emptyForm: FormState = {
    email: '',
    displayName: '',
    adminRole: 'Regional',
    adminRoles: [],
    regions: [],
    isTestConsultant: false,
    organizationId: '',
    status: 'pending',
};

const PlatformAdminManagement: React.FC<Props> = ({ currentUser, editEmail, onEditHandled }) => {
    const [admins, setAdmins] = useState<UserProfile[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [editing, setEditing] = useState(false);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    const call = async (action: string, data: Record<string, unknown>) => {
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
        return callable({ action, data });
    };

    const load = async (showLoader = true) => {
        if (showLoader)
            setLoading(true);
        try {
            const response = await call('listDirectory', {});
            const result = response.data as { admins: UserProfile[]; organizations: Organization[] };
            setAdmins(result.admins || []);
            setOrganizations(result.organizations || []);
        } finally {
            if (showLoader)
                setLoading(false);
        }
    };

    useEffect(() => {
        load().catch(error => setMessage(error.message || 'Failed loading administrators.'));
        const refresh = () => {
            if (document.visibilityState === 'visible')
                void load(false).catch(error => setMessage(error.message || 'Failed refreshing administrators.'));
        };
        const timer = window.setInterval(refresh, 30000);
        window.addEventListener('focus', refresh);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('focus', refresh);
        };
    }, [currentUser.adminRoles, currentUser.regions]);

    const edit = (admin: UserProfile) => {
        setMessage(null);
        setForm({
            email: admin.email,
            displayName: admin.displayName || '',
            adminRole: admin.adminRole || 'Regional',
            adminRoles: adminRolesFor(admin),
            regions: admin.regions || [],
            isTestConsultant: admin.isTestConsultant === true,
            organizationId: admin.organizationId || '',
            status: admin.status === 'deleted' ? 'suspended' : admin.status,
        });
        setEditing(true);
        setOpen(true);
    };

    useEffect(() => {
        if (!editEmail || loading)
            return;
        const admin = admins.find(candidate => candidate.email === editEmail);
        if (admin)
            edit(admin);
        onEditHandled?.();
    }, [editEmail, admins, loading]);

    const save = async (event: React.FormEvent) => {
        event.preventDefault();
        const error = adminAssignmentError(form.adminRoles, form.regions);
        if (error) {
            setMessage(error);
            return;
        }
        setBusy(true);
        setMessage(null);
        try {
            const { isTestConsultant, ...fields } = form;
            await call(editing ? 'updateAdmin' : 'createAdmin', {
                ...fields,
                ...(currentUser.adminRole === 'Super' ? { isTestConsultant: form.adminRoles.includes('Regional Consultant') && isTestConsultant } : {}),
            });
            await load();
            setOpen(false);
            setMessage(editing ? 'Administrator updated.' : 'Administrator invited.');
        } catch (error: any) {
            setMessage(error.message || 'Administrator update failed.');
        } finally {
            setBusy(false);
        }
    };

    const remove = async (admin: UserProfile) => {
        if (!await confirmApp(`Delete ${admin.displayName || admin.email}? Their access will be disabled.`))
            return;
        setBusy(true);
        try {
            await call('deleteAdmin', { email: admin.email });
            await load();
            setMessage('Administrator deleted.');
        } catch (error: any) {
            setMessage(error.message || 'Administrator deletion failed.');
        } finally {
            setBusy(false);
        }
    };

    const filtered = useMemo(() => admins.filter(admin => {
        const term = search.trim().toLowerCase();
        return !term || admin.email.toLowerCase().includes(term) || admin.displayName?.toLowerCase().includes(term);
    }), [admins, search]);

    const availableRoles: AdminRole[] = currentUser.adminRole === 'Super' ? ['Global', 'Regional'] : ['Regional'];

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search administrators..." className="w-72 rounded-lg border border-slate-300 px-4 py-2 text-sm" />
                <button onClick={() => { setMessage(null); setForm({ ...emptyForm, adminRole: availableRoles[0] }); setEditing(false); setOpen(true); }} className={`rounded-lg px-4 py-2 text-sm font-bold ${buttonStyles.primary}`}>Add Administrator</button>
            </div>
            {message && <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">{message}</div>}
            {loading ? (
                <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white py-24 text-slate-400">
                    <svg className="mr-3 h-8 w-8 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                    Loading administrators…
                </div>
            ) : <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-5 py-3">Administrator</th><th className="px-5 py-3">Admin Level</th><th className="px-5 py-3">Roles</th><th className="px-5 py-3">Served Regions</th><th className="px-5 py-3">Organization</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">{filtered.map(admin => {
                        const isSuper = admin.adminRole === 'Super';
                        const organization = organizations.find(candidate => candidate.id === admin.organizationId);
                        return <tr key={admin.email}><td className="px-5 py-3">{isSuper ? <div><span className="block font-bold text-slate-800">{admin.displayName}</span><span className="text-xs text-slate-400">{admin.email}</span></div> : <button onClick={() => edit(admin)} className="text-left"><span className="block font-bold text-blue-700">{admin.displayName}</span><span className="text-xs text-slate-400">{admin.email}</span></button>}</td><td className="px-5 py-3 font-semibold">{adminRoleLabel(admin.adminRole)}{hasRegionalConsultantRole(admin) && admin.isTestConsultant && <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Test Consultant</span>}</td><td className="px-5 py-3 text-xs">{adminRolesFor(admin).join(', ') || 'Not set'}</td><td className="px-5 py-3 text-xs">{hasRegionalConsultantRole(admin) ? admin.regions?.join(', ') || 'None' : 'None'}</td><td className="px-5 py-3 text-xs">{organization ? getOrganizationDisplayName(organization) : 'None'}</td><td className="px-5 py-3 capitalize">{admin.status.replace('_', ' ')}</td><td className="px-5 py-3"><div className="flex justify-end gap-3">
                            {currentUser.adminRole === 'Super' && admin.email !== currentUser.email && !admin.isDeleted && !['deleted', 'suspended'].includes(admin.status) && <button disabled={busy} onClick={() => openMfaRecovery(admin.email)} className={mfaStyles.recoveryAction}>Reset MFA</button>}
                            {isSuper ? <span className="text-xs font-semibold text-slate-400">Read only</span> : <><button onClick={() => edit(admin)} className="font-bold text-blue-600">Edit</button><button disabled={busy} onClick={() => remove(admin)} className="font-bold text-red-600 disabled:opacity-40">Delete</button></>}
                        </div></td></tr>;
                    })}</tbody>
                </table>
                {!filtered.length && <p className="p-8 text-center text-sm text-slate-400">No manageable administrators.</p>}
            </div>}

            {open && <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/60 p-4"><form onSubmit={save} className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
                <div className="mb-5 flex justify-between"><h2 className="text-xl font-extrabold">{editing ? 'Edit Administrator' : 'Add Administrator'}</h2><button type="button" onClick={() => setOpen(false)}>X</button></div>
                {message && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{message}</div>}
                <div className="grid gap-4 md:grid-cols-2">
                    <label className="text-xs font-bold uppercase text-slate-500">Name<input required value={form.displayName} onChange={event => setForm({ ...form, displayName: event.target.value })} className="mt-1 block w-full rounded-lg border px-3 py-2 text-sm" /></label>
                    <label className="text-xs font-bold uppercase text-slate-500">Email<input required type="email" disabled={editing} value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} className="mt-1 block w-full rounded-lg border px-3 py-2 text-sm disabled:bg-slate-100" /></label>
                    <label className="text-xs font-bold uppercase text-slate-500">Admin Level<select value={form.adminRole} onChange={event => setForm({ ...form, adminRole: event.target.value as AdminRole })} className="mt-1 block w-full rounded-lg border px-3 py-2 text-sm">{availableRoles.map(role => <option key={role} value={role}>{adminRoleLabel(role)}</option>)}</select></label>
                    <label className="text-xs font-bold uppercase text-slate-500">Organization<select value={form.organizationId} onChange={event => setForm({ ...form, organizationId: event.target.value })} className="mt-1 block w-full rounded-lg border px-3 py-2 text-sm"><option value="">No organization</option>{organizations.map(organization => <option key={organization.id} value={organization.id}>{getOrganizationDisplayName(organization)}</option>)}</select></label>
                    {editing && <label className="text-xs font-bold uppercase text-slate-500">Status<select value={form.status} onChange={event => setForm({ ...form, status: event.target.value as FormState['status'] })} className="mt-1 block w-full rounded-lg border px-3 py-2 text-sm"><option value="active">Active</option><option value="pending">Pending</option><option value="pending_approval">Pending Approval</option><option value="suspended">Suspended</option></select></label>}
                </div>
                <div className="mt-5"><AdminAssignmentFields adminRoles={form.adminRoles} regions={form.regions} onChange={(adminRoles, regions) => setForm({ ...form, adminRoles, regions, isTestConsultant: adminRoles.includes('Regional Consultant') && form.isTestConsultant })} /></div>
                {currentUser.adminRole === 'Super' && form.adminRoles.includes('Regional Consultant') && <label className="mt-5 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-950"><input type="checkbox" checked={form.isTestConsultant} onChange={event => setForm({ ...form, isTestConsultant: event.target.checked })} />Test Consultant<span className="font-normal">Exclude from automatic MSA assignment. Manual assignment remains available.</span></label>}
                <button disabled={busy} className="mt-6 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{editing ? 'Save Administrator' : 'Send Invitation'}</button>
            </form></div>}
        </div>
    );
};

export default PlatformAdminManagement;
